import { baseApi } from "./RootBaseApi";
import {
  GenerateHelpdeskFAQDraftsRequest,
  HelpdeskFAQ,
  PublishHelpdeskFAQTranslationRequest,
  RollbackHelpdeskFAQTranslationRequest,
  TranslateHelpdeskFAQRequest,
  TranslateHelpdeskFAQResponse,
  UpdateHelpdeskFAQTranslationDraftRequest,
} from "@/types/cms.types";

interface GetHelpdeskFAQsParams {
  search?: string;
  language?: string;
  page?: number;
  limit?: number;
}

interface GetHelpdeskFAQsResponse {
  status: boolean;
  message: string;
  data: HelpdeskFAQ[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface GetHelpdeskFAQByIdResponse {
  status: boolean;
  message: string;
  data: HelpdeskFAQ;
}

interface CreateHelpdeskFAQRequest {
  title: string;
  body: string;
  source_language: string;
  translations?: Record<string, { title: string; body: string }>;
}

interface CreateHelpdeskFAQResponse {
  status: boolean;
  message: string;
  data: HelpdeskFAQ;
}

interface UpdateHelpdeskFAQRequest {
  id: string;
  title?: string;
  body?: string;
  source_language?: string;
  translations?: Record<string, { title: string; body: string }>;
}

interface DeleteHelpdeskFAQResponse {
  status: boolean;
  message: string;
}

export const helpdeskFAQApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getHelpdeskFAQs: builder.query<
      GetHelpdeskFAQsResponse,
      GetHelpdeskFAQsParams
    >({
      query: (params) => ({
        url: "/faqs/admin/list",
        method: "GET",
        params: {
          search: params.search,
          language: params.language,
          page: params.page,
          limit: params.limit,
        },
      }),
      providesTags: ["HelpdeskFAQ"],
    }),
    getHelpdeskFAQById: builder.query<GetHelpdeskFAQByIdResponse, string>({
      query: (id) => ({
        url: `/faqs/${id}/admin`,
        method: "GET",
      }),
      providesTags: (_result, _error, id) => [
        { type: "HelpdeskFAQ" as const, id },
      ],
    }),
    createHelpdeskFAQ: builder.mutation<
      CreateHelpdeskFAQResponse,
      CreateHelpdeskFAQRequest
    >({
      query: (body) => ({
        url: "/faqs",
        method: "POST",
        data: body,
      }),
      invalidatesTags: ["HelpdeskFAQ"],
    }),
    updateHelpdeskFAQ: builder.mutation<
      CreateHelpdeskFAQResponse,
      UpdateHelpdeskFAQRequest
    >({
      query: ({ id, ...body }) => ({
        url: `/faqs/${id}`,
        method: "PATCH",
        data: body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "HelpdeskFAQ" as const, id },
        "HelpdeskFAQ",
      ],
    }),
    deleteHelpdeskFAQ: builder.mutation<DeleteHelpdeskFAQResponse, string>({
      query: (id) => ({
        url: `/faqs/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["HelpdeskFAQ"],
    }),
    translateHelpdeskFAQ: builder.mutation<
      TranslateHelpdeskFAQResponse,
      TranslateHelpdeskFAQRequest
    >({
      query: (body) => ({
        url: "/faqs/translate",
        method: "POST",
        data: body,
      }),
    }),
    generateHelpdeskFAQDrafts: builder.mutation<
      CreateHelpdeskFAQResponse,
      GenerateHelpdeskFAQDraftsRequest
    >({
      query: ({ id, ...body }) => ({
        url: `/faqs/${id}/translations/generate`,
        method: "POST",
        data: body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "HelpdeskFAQ" as const, id },
        "HelpdeskFAQ",
      ],
    }),
    updateHelpdeskFAQTranslationDraft: builder.mutation<
      CreateHelpdeskFAQResponse,
      UpdateHelpdeskFAQTranslationDraftRequest
    >({
      query: ({ id, language, ...body }) => ({
        url: `/faqs/${id}/translations/${language}/draft`,
        method: "PATCH",
        data: body,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "HelpdeskFAQ" as const, id },
        "HelpdeskFAQ",
      ],
    }),
    publishHelpdeskFAQTranslation: builder.mutation<
      CreateHelpdeskFAQResponse,
      PublishHelpdeskFAQTranslationRequest
    >({
      query: ({ id, language }) => ({
        url: `/faqs/${id}/translations/${language}/publish`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "HelpdeskFAQ" as const, id },
        "HelpdeskFAQ",
      ],
    }),
    rollbackHelpdeskFAQTranslation: builder.mutation<
      CreateHelpdeskFAQResponse,
      RollbackHelpdeskFAQTranslationRequest
    >({
      query: ({ id, language }) => ({
        url: `/faqs/${id}/translations/${language}/rollback`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: "HelpdeskFAQ" as const, id },
        "HelpdeskFAQ",
      ],
    }),
  }),
});

export const {
  useGetHelpdeskFAQsQuery,
  useGetHelpdeskFAQByIdQuery,
  useCreateHelpdeskFAQMutation,
  useUpdateHelpdeskFAQMutation,
  useDeleteHelpdeskFAQMutation,
  useTranslateHelpdeskFAQMutation,
  useGenerateHelpdeskFAQDraftsMutation,
  useUpdateHelpdeskFAQTranslationDraftMutation,
  usePublishHelpdeskFAQTranslationMutation,
  useRollbackHelpdeskFAQTranslationMutation,
} = helpdeskFAQApi;
